import Modal from 'antd/es/modal/Modal';

import type { Webhook } from 'api';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';

import type { ToggleMode } from './constants';
import {
  TOGGLE_WEBHOOK_STATUS_BLOCKED_ROW_TEST_ID,
  TOGGLE_WEBHOOK_STATUS_CANCEL_TEST_ID,
  TOGGLE_WEBHOOK_STATUS_CONFIRM_TEST_ID,
  TOGGLE_WEBHOOK_STATUS_MODAL_TEST_ID
} from './constants';
import { useToggleWebhookStatus } from './useToggleWebhookStatus';
import {
  activateBlockedByKeyIntro,
  activateBlockedTitle,
  toggleWebhookConfirmLabel,
  toggleWebhookTitle
} from './utils';

import { useAuthenticationKeys } from '../hooks/useAuthenticationKeys';
import { webhookLabel } from '../utils/webhookLabel';

type ToggleWebhookStatusModalProps = {
  webhooks: Webhook[];
  mode: ToggleMode;
  open: boolean;
  onCancel: () => void;
  onConfirmed: () => void;
};

const MODAL_WIDTH = 480;

const Body = ({ mode, count }: { mode: ToggleMode; count: number }) => {
  if (mode === 'activate') {
    return count === 1 ? (
      <p>
        Are you sure you want to activate this webhook?
        <br />
        <br />
        Event deliveries to this endpoint will resume immediately.
      </p>
    ) : (
      <p>
        Are you sure you want to activate <strong>{count} webhooks</strong>?
        <br />
        <br />
        Event deliveries to these endpoints will resume immediately.
      </p>
    );
  }
  return count === 1 ? (
    <p>
      Are you sure you want to deactivate this webhook?
      <br />
      <br />
      Deactivation will immediately stop all event deliveries to this endpoint.
      Any events that occur while the webhook is inactive will not be resent.
      <br />
      <br />
      To reactivate it, return to the webhook details and click{' '}
      <strong>Activate</strong>.
    </p>
  ) : (
    <p>
      Are you sure you want to deactivate <strong>{count} webhooks</strong>?
      <br />
      <br />
      Deactivation will immediately stop all event deliveries to these
      endpoints. Any events that occur while the webhooks are inactive will not
      be resent.
      <br />
      <br />
      To reactivate them, return to each webhook&rsquo;s details and click{' '}
      <strong>Activate</strong>.
    </p>
  );
};

const BlockedByKey = ({ webhooks }: { webhooks: Webhook[] }) => (
  <>
    <p>{activateBlockedByKeyIntro(webhooks.length)}</p>
    <ul className="flex list-disc flex-col gap-1 pl-5">
      {webhooks.map((webhook) => (
        <li
          key={webhook.uuid}
          data-testid={TOGGLE_WEBHOOK_STATUS_BLOCKED_ROW_TEST_ID(webhook.uuid)}
          className="break-all"
        >
          {webhookLabel(webhook)}
        </li>
      ))}
    </ul>
  </>
);

export const ToggleWebhookStatusModal = ({
  webhooks,
  mode,
  open,
  onCancel,
  onConfirmed
}: ToggleWebhookStatusModalProps) => {
  const { toggle, isPending } = useToggleWebhookStatus();
  const { keys } = useAuthenticationKeys();
  const disabledKeyUuids = new Set(
    keys.filter((key) => !key.enabled).map((key) => key.uuid)
  );
  const blocked =
    mode === 'activate'
      ? webhooks.filter((webhook) =>
          disabledKeyUuids.has(webhook.auth_uuid ?? '')
        )
      : [];
  const toToggle = webhooks.filter((webhook) => !blocked.includes(webhook));
  const count = toToggle.length;
  const allBlocked = count === 0 && blocked.length > 0;

  const handleConfirm = () => toggle(toToggle, mode, onConfirmed);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width={MODAL_WIDTH}
      destroyOnHidden
      title={
        <div className="text-lg font-medium text-content-primary">
          {allBlocked
            ? activateBlockedTitle(blocked.length)
            : toggleWebhookTitle(mode, count)}
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <SecondaryButton
            data-testid={TOGGLE_WEBHOOK_STATUS_CANCEL_TEST_ID}
            onClick={onCancel}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            danger={mode === 'deactivate'}
            type={mode === 'deactivate' ? 'default' : 'primary'}
            data-testid={TOGGLE_WEBHOOK_STATUS_CONFIRM_TEST_ID}
            disabled={isPending || count === 0}
            onClick={handleConfirm}
          >
            {toggleWebhookConfirmLabel(mode, count)}
          </PrimaryButton>
        </div>
      }
    >
      <div
        data-testid={TOGGLE_WEBHOOK_STATUS_MODAL_TEST_ID}
        className="flex flex-col gap-2 text-base text-content-primary"
      >
        {count > 0 && <Body mode={mode} count={count} />}
        {blocked.length > 0 && <BlockedByKey webhooks={blocked} />}
      </div>
    </Modal>
  );
};
